import React from 'react';
import { useHistory } from 'react-router';
import { ChevronRight } from '@mui/icons-material';
import { styled } from '@mui/material';
import { Colors } from 'library';
import { Avatar, Card, Chip, ClickableSurface, Divider, Row, Stack, Text, Title } from 'library/ui';
import { useClient } from 'shared';

interface DashboardApplicant {
    id: number;
    applied_user: {
        id: number;
        name: string;
        pictureUrl?: string;
        credentials: string[];
    };
    job: number;
    viewed: boolean;
}

interface DashboardApplicantSummary {
    job: number;
    headline: string;
    location_name?: string;
    manual_clearinghouse_check?: boolean;
    applicants: DashboardApplicant[];
}

export function ApplicantsToReview() {
    const { data } = useClient<DashboardApplicantSummary[]>({ url: 'api/applicant-approval/dashboard-summary/' });
    const jobs = (data ?? []).filter((summary) => summary.applicants.length > 0);

    if (jobs.length === 0) return null;

    return (
        <Card padding="sm">
            <Stack gap="none" padding="none">
                <Stack gap="none" padding="sm">
                    <Title size="h2">Applicants to review</Title>
                    <Text size="sm" tone="secondary">
                        Substitutes are available for your upcoming shifts. Book them to secure coverage.
                    </Text>
                </Stack>
                {jobs.map((summary) => (
                    <JobApplicantsRow key={summary.job} summary={summary} />
                ))}
            </Stack>
        </Card>
    );
}

function JobApplicantsRow({ summary }: { summary: DashboardApplicantSummary }) {
    const history = useHistory();
    const newApprovalsCount = summary.applicants.filter((a) => !a.viewed).length;
    const awaitingConfirmationCount = summary.applicants.length - newApprovalsCount;

    const subtitleParts = [];
    if (newApprovalsCount > 0) {
        subtitleParts.push(`${newApprovalsCount} interested substitute${newApprovalsCount === 1 ? '' : 's'}`);
        if (!summary.manual_clearinghouse_check) subtitleParts.push('Near matches · review to fill');
    }
    if (awaitingConfirmationCount > 0) {
        subtitleParts.push(
            `${awaitingConfirmationCount} approval${awaitingConfirmationCount === 1 ? '' : 's'} awaiting acceptance`,
        );
    }

    return (
        <>
            <Divider />
            <ClickableSurface onClick={() => history.push(`/jobs/${summary.job}/applicants`)}>
                <Row gap="sm" align="center" fullWidth>
                    <Stack gap="sm" grow padding="none">
                        <Stack gap="none">
                            <Text weight="bold">{summary.headline}</Text>
                            {summary.location_name && (
                                <Text size="sm" tone="secondary">
                                    {summary.location_name}
                                </Text>
                            )}
                            <Text size="sm" tone="secondary">
                                {subtitleParts.join(' · ')}
                            </Text>
                        </Stack>
                        <Row gap="sm" align="center">
                            <Row gap="none" align="center">
                                {summary.applicants.map((applicant, i) => (
                                    <ClusterAvatar
                                        key={applicant.id}
                                        imageUrl={applicant.applied_user.pictureUrl}
                                        name={applicant.applied_user.name}
                                        size={32}
                                        $overlap={i > 0}
                                    />
                                ))}
                            </Row>
                            {newApprovalsCount > 0 && (
                                <Chip variant="filled" size="small" bgColor={Colors.accent}>
                                    New!
                                </Chip>
                            )}
                        </Row>
                    </Stack>
                    <ChevronRight htmlColor={Colors.textSecondary} />
                </Row>
            </ClickableSurface>
        </>
    );
}

const ClusterAvatar = styled(Avatar, { shouldForwardProp: (prop) => prop !== '$overlap' })<{ $overlap: boolean }>(
    ({ $overlap }) => ({
        marginLeft: $overlap ? -10 : 0,
    }),
);
